import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import toast from 'react-hot-toast';
import { 
  BookOpen, 
  Wand2, 
  Palette, 
  Target, 
  Download,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Clock,
  Loader,
  Sparkles,
  FileText,
  Image,
  BarChart3,
  Coins,
  AlertTriangle,
  Eye
} from 'lucide-react';

const BookCreationWizard = () => {
  const { user, API_BASE_URL, credits, refreshCredits, checkCreditsForOperation } = useAuth();
  const navigate = useNavigate();
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [bookId, setBookId] = useState(null);
  const [book, setBook] = useState(null);
  
  // Form data
  const [bookForm, setBookForm] = useState({
    title: '',
    chapters: 5,
    genre: 'General',
    writing_style: '',
    themes: '',
    description: ''
  });
  
  // Loading states
  const [generatingBook, setGeneratingBook] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [optimizingMetadata, setOptimizingMetadata] = useState(false);
  
  // Results
  const [optimizationResult, setOptimizationResult] = useState(null);
  
  // Credit checks
  const [creditChecks, setCreditChecks] = useState({});

  const genres = [
    'General', 'Fiction', 'Non-Fiction', 'Business', 'Self-Help',
    'Romance', 'Mystery', 'Thriller', 'Science Fiction', 'Fantasy',
    'Horror', 'Biography', 'History', 'Travel', 'Cooking',
    'Technology', 'Health', 'Education', 'Children', 'Poetry'
  ];

  const writingStyles = [
    'Descriptive', 'Narrative', 'Conversational', 'Academic', 'Poetic',
    'Journalistic', 'Technical', 'Humorous', 'Formal', 'Informal'
  ];

  const themes = [
    'Love', 'Friendship', 'Betrayal', 'Redemption', 'Coming of Age',
    'Good vs Evil', 'Power', 'Identity', 'Family', 'Loss',
    'Justice', 'Freedom', 'Nature', 'Technology', 'Society',
    'War', 'Peace', 'Faith', 'Change'
  ];

  const steps = [
    { id: 1, title: 'Book Details', icon: BookOpen, description: 'Title, genre, and chapters' },
    { id: 2, title: 'Generate Content', icon: Wand2, description: 'AI writes your book' },
    { id: 3, title: 'Create Cover', icon: Palette, description: 'Professional book cover' },
    { id: 4, title: 'Optimize Metadata', icon: Target, description: 'SEO and marketing copy' },
    { id: 5, title: 'Download & Publish', icon: Download, description: 'Get your files' }
  ];

  useEffect(() => {
    loadCreditChecks();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (bookId) {
      fetchBook();
    }
  }, [bookId]);

  const loadCreditChecks = async () => {
    // First refresh credits to get latest balance
    if (refreshCredits) {
      await refreshCredits();
    }
    
    const operations = ['book_generation', 'cover_generation', 'metadata_optimization'];
    const checks = {};
    
    for (const operation of operations) {
      if (checkCreditsForOperation) {
        checks[operation] = await checkCreditsForOperation(operation);
      }
    }
    
    setCreditChecks(checks);
  };

  const fetchBook = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const bookData = await response.json();
        setBook(bookData);
      }
    } catch (error) {
      console.error('Error fetching book:', error);
    }
  };

  const handleBookFormChange = (e) => {
    const { name, value } = e.target;
    setBookForm(prev => ({
      ...prev,
      [name]: name === 'chapters' ? parseInt(value) : value
    }));
  };

  const generateBook = async () => {
    if (!bookForm.title.trim()) {
      toast.error('Please enter a book title');
      return;
    }

    if (!creditChecks.book_generation?.sufficient) {
      toast.error('Insufficient credits for book generation');
      return;
    }

    setGeneratingBook(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookForm)
      });

      if (response.ok) {
        const data = await response.json();
        setBookId(data.id);
        setBook(data);
        toast.success('Book generation started!');
        setCurrentStep(2);
        
        // Refresh credits
        if (refreshCredits) {
          await refreshCredits();
        }
        
        // Monitor book completion
        monitorBookGeneration(data.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate book');
      }
    } catch (error) {
      console.error('Error generating book:', error);
      toast.error('Failed to generate book');
    } finally {
      setGeneratingBook(false);
    }
  };

  const monitorBookGeneration = async (id) => {
    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/books/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const bookData = await response.json();
          setBook(bookData);
          
          if (bookData.status === 'completed') {
            toast.success('Book generation completed!');
            return;
          } else if (bookData.status === 'failed') {
            toast.error('Book generation failed');
            return;
          }
        }
      } catch (error) {
        console.error('Error checking book status:', error);
      }
      
      // Check again in 5 seconds
      setTimeout(checkStatus, 5000);
    };
    
    setTimeout(checkStatus, 5000);
  };

  const generateCover = async () => {
    if (!bookId || !creditChecks.cover_generation?.sufficient) {
      toast.error('Cannot generate cover');
      return;
    }

    setGeneratingCover(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/generate-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: book.title,
          author_name: user.full_name,
          genre: book.genre,
          provider: 'fal',
          number_of_variations: 3
        })
      });

      if (response.ok) {
        toast.success('Cover generated successfully!');
        await fetchBook();
        await refreshCredits();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate cover');
      }
    } catch (error) {
      console.error('Error generating cover:', error);
      toast.error('Failed to generate cover');
    } finally {
      setGeneratingCover(false);
    }
  };

  const optimizeMetadata = async () => {
    if (!book || !creditChecks.metadata_optimization?.sufficient) {
      toast.error('Cannot optimize metadata');
      return;
    }

    setOptimizingMetadata(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/optimize-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOptimizationResult(data.optimization);
        toast.success('Metadata optimized successfully!');
        await refreshCredits();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to optimize metadata');
      }
    } catch (error) {
      console.error('Error optimizing metadata:', error);
      toast.error('Failed to optimize metadata');
    } finally {
      setOptimizingMetadata(false);
    }
  };

  const downloadBook = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/download/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        const binaryString = atob(data.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.content_type });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`${format.toUpperCase()} downloaded successfully!`);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download book');
    }
  };

  const canProceedToStep = (step) => {
    switch (step) {
      case 2:
        return bookForm.title && bookForm.genre;
      case 3:
        return book && book.status === 'completed';
      case 4:
        return book && book.has_cover;
      case 5:
        return book && optimizationResult;
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Book Title *
              </label>
              <input
                type="text"
                name="title"
                value={bookForm.title}
                onChange={handleBookFormChange}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                placeholder="Enter your book title"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Genre *
                </label>
                <select
                  name="genre"
                  value={bookForm.genre}
                  onChange={handleBookFormChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                >
                  {genres.map(genre => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Number of Chapters
                </label>
                <select
                  name="chapters"
                  value={bookForm.chapters}
                  onChange={handleBookFormChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                >
                  {[3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num} chapters</option>
                  ))}
                </select>
                <p className="text-gray-400 text-sm mt-2">
                  Start with fewer chapters for faster generation. You can add more chapters later in the book manager.
                </p>
              </div>
            </div>

            {/* Writing Style and Themes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Writing Style (Optional)
                </label>
                <select
                  name="writing_style"
                  value={bookForm.writing_style}
                  onChange={handleBookFormChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select writing style...</option>
                  {writingStyles.map(style => (
                    <option key={style} value={style}>{style}</option>
                  ))}
                </select>
                <p className="text-gray-400 text-sm mt-2">
                  Choose a writing style to influence the tone and voice of your book.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Themes (Optional)
                </label>
                <select
                  name="themes"
                  value={bookForm.themes}
                  onChange={handleBookFormChange}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select theme...</option>
                  {themes.map(theme => (
                    <option key={theme} value={theme}>{theme}</option>
                  ))}
                </select>
                <p className="text-gray-400 text-sm mt-2">
                  Add thematic elements to guide the content direction.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Book Description
              </label>
              <textarea
                name="description"
                value={bookForm.description}
                onChange={handleBookFormChange}
                rows={3}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                placeholder="Brief description of your book's theme or content"
              />
            </div>

            {/* Credit Cost */}
            {creditChecks.book_generation && (
              <div className={`p-4 rounded-lg border ${
                creditChecks.book_generation.sufficient 
                  ? 'bg-blue-500/20 border-blue-500/30' 
                  : 'bg-red-500/20 border-red-500/30'
              }`}>
                <div className="flex items-center space-x-2">
                  <Coins className={`h-5 w-5 ${
                    creditChecks.book_generation.sufficient ? 'text-blue-400' : 'text-red-400'
                  }`} />
                  <span className={`font-medium ${
                    creditChecks.book_generation.sufficient ? 'text-blue-300' : 'text-red-300'
                  }`}>
                    {creditChecks.book_generation.sufficient 
                      ? `This will cost ${creditChecks.book_generation.required} credits`
                      : `Insufficient credits! Need ${creditChecks.book_generation.required}, have ${creditChecks.book_generation.current_balance}`
                    }
                  </span>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            {book && book.status === 'generating' && (
              <div className="text-center py-12">
                <Loader className="h-16 w-16 text-purple-400 mx-auto mb-4 animate-spin" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  AI is writing your book...
                </h3>
                <p className="text-gray-400">
                  This may take a few minutes. Your book will have {book.chapters} chapters.
                </p>
              </div>
            )}

            {book && book.status === 'completed' && (
              <div className="text-center py-12">
                <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Book Generated Successfully!
                </h3>
                <p className="text-gray-400 mb-6">
                  "{book.title}" is ready with {book.chapters} chapters
                </p>
                <button
                  onClick={() => navigate(`/books/${bookId}`)}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center space-x-2 mx-auto"
                >
                  <Eye className="h-4 w-4" />
                  <span>Preview Book</span>
                </button>
              </div>
            )}

            {!book && (
              <div className="text-center py-12">
                <button
                  onClick={generateBook}
                  disabled={generatingBook || !canProceedToStep(2) || !creditChecks.book_generation?.sufficient}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-colors flex items-center space-x-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingBook ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Generating Book...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-5 w-5" />
                      <span>Generate My Book</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            {book && book.covers && book.covers.length > 0 ? (
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Your Book Covers</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {book.covers.map((cover, index) => (
                    <div key={index} className="bg-gray-800/50 rounded-lg p-4">
                      <div className="aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden mb-4 flex items-center justify-center">
                        <img
                          src={`data:image/png;base64,${cover.image_base64}`}
                          alt={`Cover variation ${index + 1}`}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Palette className="h-16 w-16 text-purple-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Create Professional Cover
                </h3>
                <p className="text-gray-400 mb-6">
                  Generate a KDP-compliant cover for your book
                </p>
                
                {creditChecks.cover_generation && (
                  <div className={`p-4 rounded-lg border mb-6 ${
                    creditChecks.cover_generation.sufficient 
                      ? 'bg-blue-500/20 border-blue-500/30' 
                      : 'bg-red-500/20 border-red-500/30'
                  }`}>
                    <div className="flex items-center justify-center space-x-2">
                      <Coins className={`h-5 w-5 ${
                        creditChecks.cover_generation.sufficient ? 'text-blue-400' : 'text-red-400'
                      }`} />
                      <span className={`font-medium ${
                        creditChecks.cover_generation.sufficient ? 'text-blue-300' : 'text-red-300'
                      }`}>
                        {creditChecks.cover_generation.sufficient 
                          ? `This will cost ${creditChecks.cover_generation.required} credits`
                          : `Insufficient credits! Need ${creditChecks.cover_generation.required}, have ${creditChecks.cover_generation.current_balance}`
                        }
                      </span>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={generateCover}
                  disabled={generatingCover || !canProceedToStep(3) || !creditChecks.cover_generation?.sufficient}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-pink-600 hover:to-purple-600 transition-colors flex items-center space-x-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingCover ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Generating Cover...</span>
                    </>
                  ) : (
                    <>
                      <Palette className="h-5 w-5" />
                      <span>Generate Cover</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {optimizationResult ? (
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Optimized Metadata</h3>
                <div className="space-y-4">
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Optimized Title</h4>
                    <p className="text-gray-300">{optimizationResult.optimized_title}</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Amazon Description</h4>
                    <p className="text-gray-300 whitespace-pre-wrap">{optimizationResult.amazon_description}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Target className="h-16 w-16 text-purple-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  Optimize for Success
                </h3>
                <p className="text-gray-400 mb-6">
                  AI-powered metadata optimization for better discoverability
                </p>
                
                {creditChecks.metadata_optimization && (
                  <div className={`p-4 rounded-lg border mb-6 ${
                    creditChecks.metadata_optimization.sufficient 
                      ? 'bg-blue-500/20 border-blue-500/30' 
                      : 'bg-red-500/20 border-red-500/30'
                  }`}>
                    <div className="flex items-center justify-center space-x-2">
                      <Coins className={`h-5 w-5 ${
                        creditChecks.metadata_optimization.sufficient ? 'text-blue-400' : 'text-red-400'
                      }`} />
                      <span className={`font-medium ${
                        creditChecks.metadata_optimization.sufficient ? 'text-blue-300' : 'text-red-300'
                      }`}>
                        {creditChecks.metadata_optimization.sufficient 
                          ? `This will cost ${creditChecks.metadata_optimization.required} credits`
                          : `Insufficient credits! Need ${creditChecks.metadata_optimization.required}, have ${creditChecks.metadata_optimization.current_balance}`
                        }
                      </span>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={optimizeMetadata}
                  disabled={optimizingMetadata || !canProceedToStep(4) || !creditChecks.metadata_optimization?.sufficient}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-colors flex items-center space-x-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {optimizingMetadata ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Optimizing...</span>
                    </>
                  ) : (
                    <>
                      <Target className="h-5 w-5" />
                      <span>Optimize Metadata</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-white mb-2">
                Your Book is Ready!
              </h3>
              <p className="text-gray-400">
                Download your files and start publishing
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* KDP Publishing */}
              <div className="bg-gray-800/50 rounded-lg p-6 border border-purple-500/20">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <BookOpen className="h-5 w-5 mr-2" />
                  KDP Publishing
                </h4>
                <p className="text-gray-400 text-sm mb-4">
                  Professional formats optimized for Amazon Kindle Direct Publishing
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => downloadBook('pdf')}
                    className="w-full bg-red-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Download PDF (Print)</span>
                  </button>
                  <button
                    onClick={() => downloadBook('epub')}
                    className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Download EPUB (Kindle)</span>
                  </button>
                </div>
              </div>

              {/* General Use */}
              <div className="bg-gray-800/50 rounded-lg p-6 border border-blue-500/20">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  General Reading
                </h4>
                <p className="text-gray-400 text-sm mb-4">
                  Clean formats for personal use, sharing, or other platforms
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => downloadBook('docx')}
                    className="w-full bg-green-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Download DOCX (Editable)</span>
                  </button>
                  <button
                    onClick={() => navigate(`/books/${bookId}`)}
                    className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View & Edit Book</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEOHead 
        title="Book Creation Wizard - Create Your Book Step by Step"
        description="Complete book creation workflow from manuscript generation to publishing. Create books, covers, and metadata with AI assistance. Perfect for KDP authors."
        keywords="book creation wizard, AI book creation, step by step book publishing, book workflow, KDP book creation, AI book wizard"
        canonical="/wizard"
        ogTitle="Book Creation Wizard - From Idea to Published Book"
        ogDescription="Complete guided workflow to create professional books with AI. Generate manuscripts, covers, and metadata in one place."
        ogImage="/book-creation-wizard.png"
        ogUrl="/wizard"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Book Creation Wizard",
          "description": "Step-by-step book creation and publishing workflow",
          "applicationCategory": "WritingApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free trial with credits"
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            Create Your Book
          </h1>
          <p className="text-xl text-gray-100 drop-shadow-md">
            From idea to published book in minutes
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all ${
                  currentStep >= step.id
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'bg-gray-800 border-gray-600 text-gray-400'
                }`}>
                  {currentStep > step.id ? (
                    <CheckCircle className="h-6 w-6" />
                  ) : (
                    <step.icon className="h-6 w-6" />
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-full h-1 mx-4 transition-all ${
                    currentStep > step.id ? 'bg-purple-600' : 'bg-gray-600'
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          <div className="flex justify-between mt-4">
            {steps.map((step) => (
              <div key={step.id} className="text-center max-w-[120px]">
                <h3 className={`text-sm font-medium drop-shadow-md ${
                  currentStep >= step.id ? 'text-white' : 'text-gray-300'
                }`}>
                  {step.title}
                </h3>
                <p className="text-xs text-gray-400 mt-1 drop-shadow-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-gray-900/50 rounded-2xl p-8 border border-purple-500/20 mb-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="flex items-center space-x-2 px-6 py-3 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Previous</span>
          </button>

          {currentStep < 5 && (
            <button
              onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
              disabled={!canProceedToStep(currentStep + 1)}
              className="flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default BookCreationWizard;